import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EventsTable = ({ events, onStatusChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const navigate = useNavigate();

  const statusOptions = [
    { value: 'all', label: 'All Events' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'live', label: 'Live' },
    { value: 'ended', label: 'Ended' }
  ];

  const sortOptions = [
    { value: 'date', label: 'Event Date' },
    { value: 'created', label: 'Created Date' },
    { value: 'sales', label: 'Total Sales' },
    { value: 'name', label: 'Event Name' }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-muted text-muted-foreground', label: 'Draft' },
      published: { color: 'bg-primary text-primary-foreground', label: 'Published' },
      live: { color: 'bg-success text-success-foreground', label: 'Live' },
      ended: { color: 'bg-secondary text-secondary-foreground', label: 'Ended' }
    };

    const config = statusConfig?.[status] || statusConfig?.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const filteredEvents = events?.filter(event => {
      const matchesSearch = event?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           event?.venue?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesStatus = statusFilter === 'all' || event?.status === statusFilter;
      return matchesSearch && matchesStatus;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(a.date) - new Date(b.date);
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'sales':
          return b?.totalSales - a?.totalSales;
        case 'name':
          return a?.name?.localeCompare(b?.name);
        default:
          return 0;
      }
    });

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-foreground">Events Management</h3>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => navigate('/create-event')}
          >
            Create Event
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search events or venues..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <div className="flex gap-2">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
              className="w-40"
            />
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
              className="w-40"
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Event</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Date & Venue</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Sales</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Tickets Sold</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents?.map((event) => (
              <tr key={event?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={event?.image}
                        alt={event?.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/assets/images/no_image.png';
                        }}
                      />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{event?.name}</h4>
                      <p className="text-sm text-muted-foreground">{event?.category}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {new Date(event.date)?.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                    <p className="text-sm text-muted-foreground">{event?.venue}</p>
                  </div>
                </td>
                <td className="p-4">
                  {getStatusBadge(event?.status)}
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">${event?.totalSales?.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                  </div>
                </td>
                <td className="p-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{event?.ticketsSold}</p>
                    <p className="text-sm text-muted-foreground">of {event?.totalTickets}</p>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => navigate(`/event-detail/${event?.id}`)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => navigate(`/edit-event/${event?.id}`)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="MoreHorizontal"
                      onClick={() => {}}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredEvents?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No events found</h4>
          <p className="text-muted-foreground mb-4">
            {searchTerm || statusFilter !== 'all' ?'Try adjusting your search or filter criteria' :'Create your first event to get started'
            }
          </p>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => navigate('/create-event')}
          >
            Create Event
          </Button>
        </div>
      )}
    </div>
  );
};

export default EventsTable;